import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PreferencesCard,
  PREFERENCE_LANGUAGES,
  Tabs,
  type Tab,
  PageHeader,
} from '@airclou/ui'
import { useUserPreferences, resolveDisplayLanguage } from '@airclou/shell'
import { useRoutedTabs } from '@/hooks/useRoutedTabs'
import { ProfileTab } from './ProfileTab'

/**
 * One list, so a tab cannot be added to the strip without also being a URL the
 * page will route to — `tabs` below is typed against it, so a mismatch is a
 * compile error rather than a tab that silently falls back to Profile.
 */
const ACCOUNT_TABS = ['profile', 'preferences'] as const
type AccountTab = (typeof ACCOUNT_TABS)[number]

/**
 * The one personal page in a console whose every other page is about the
 * tenant. It lives at `/account`, outside `/settings`, because `/settings` is
 * the organization page: nesting a per-user page under it made the sidebar
 * highlight "Organization" while the operator was editing their own photo.
 * Its entry point is the avatar menu, and nothing in the sidebar points here.
 *
 * Profile and Preferences used to be two separate pages reached from two
 * separate menu entries, which left the editable version of a person on one
 * page and a read-only version on the other — email appeared on both, and
 * nothing said which one won.
 */
export function AccountPage() {
  const { t, i18n } = useTranslation('dashboard')
  const { preferences, setPreference, saving, lastSaveError, appSlug } = useUserPreferences()

  // Not `preferences.language`: with nothing chosen that resolves to the 'en'
  // default while i18next may have detected the browser's `zh`, and the select
  // would read "English" on a page rendered entirely in Chinese. The rule lives
  // in @airclou/shell so all three Preferences pages agree — this derivation was
  // written here first and the other two were missed, which is the whole reason
  // it is shared now.
  const effectiveLanguage = resolveDisplayLanguage({
    i18n,
    appSlug,
    preferenceLanguage: preferences.language,
    supported: PREFERENCE_LANGUAGES,
  })

  const { activeTab, handleTabChange } = useRoutedTabs({
    basePath: '/account',
    validTabs: [...ACCOUNT_TABS],
    defaultTab: ACCOUNT_TABS[0],
  })

  const tabs: (Tab & { id: AccountTab })[] = useMemo(() => [
    {
      id: 'profile',
      label: t('account.profile'),
      content: <ProfileTab />,
    },
    {
      id: 'preferences',
      label: t('account.preferences'),
      content: (
        // Theme is the reason this tab exists: dashboard had a full dark
        // palette and no way to reach it. Instant-save, per the settings
        // model — these are personal and cheaply reversible. (Not per-device:
        // all three are account values, held by Auth.)
        <div className="max-w-2xl">
          <PreferencesCard
            // The provider already put the value back; this is what tells the
            // person so, instead of a control that silently snaps back.
            saveError={lastSaveError ? t('account.preferenceSaveFailed') : null}
            // packages/ui carries no i18n of its own, so a shared component
            // takes its copy as props. Without this the tab label above the
            // card was translated and the card's own labels were not.
            labels={{
              title: t('account.preferences'),
              theme: t('account.theme'),
              language: t('account.language'),
              timezone: t('account.timezone'),
              themeSystem: t('account.themeSystem'),
              themeLight: t('account.themeLight'),
              themeDark: t('account.themeDark'),
              systemHint: (resolved) => t('account.themeSystemHint', { resolved }),
            }}
            theme={preferences.theme}
            language={effectiveLanguage}
            timezone={preferences.timezone}
            onThemeChange={(v) => setPreference('theme', v)}
            onLanguageChange={(v) => setPreference('language', v)}
            onTimezoneChange={(v) => setPreference('timezone', v)}
            disabled={saving}
          />
        </div>
      ),
    },
  ], [preferences, effectiveLanguage, setPreference, saving, lastSaveError, t])

  return (
    <div className="space-y-6">
      <PageHeader title={t('account.title')} description={t('account.subtitle')} />
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />
    </div>
  )
}
